/*
 * Metadata snapshots browser — platform-admin overview of which workspaces
 * have synced tracker / aggregate metadata, what version they're on, and
 * the entity counts in each. Each row is the current snapshot stored
 * server-side; full historical version diffing is a future enhancement
 * (today the platform retains the last synced snapshot per workspace).
 *
 * Data flow: list workspaces with snapshots, then fetch summary per
 * (workspace, type) in parallel — same lazy pattern AdminDhisMetadata
 * uses on its overview drawer.
 */

import { useEffect, useState } from "react";
import PageBreadcrumbs from "../../layouts/PageBreadcrumbs.js";
import { api, ApiError } from "../../lib/api.js";

type Kind = "tracker" | "aggregate";

interface SnapshotRow {
  workspaceId: string;
  kind: Kind;
  version?: number;
  createdAt?: string;
  totalEntities: number;
  topCounts: Array<{ section: string; count: number }>;
}

export default function AdminMetadataSnapshots() {
  const [rows, setRows] = useState<SnapshotRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function refresh() {
    setError(null);
    setRows(null);
    try {
      const [trackerWs, aggregateWs] = await Promise.all([
        api.trackerMetadataWorkspaces(),
        api.aggregateMetadataWorkspaces()
      ]);
      const targets: Array<{ workspaceId: string; kind: Kind }> = [
        ...trackerWs.workspaces.map((w) => ({ workspaceId: w, kind: "tracker" as const })),
        ...aggregateWs.workspaces.map((w) => ({ workspaceId: w, kind: "aggregate" as const }))
      ];
      const fetched = await Promise.all(targets.map(async (t) => {
        const summary = t.kind === "tracker"
          ? await api.trackerMetadataSummary(t.workspaceId).catch(() => null)
          : await api.aggregateMetadataSummary(t.workspaceId).catch(() => null);
        const s = summary?.summary ?? null;
        const counts = (s?.counts ?? {}) as Record<string, number>;
        const total = Object.values(counts).reduce((a, b) => a + b, 0);
        const top = Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 4)
          .map(([section, count]) => ({ section, count }));
        return {
          workspaceId: t.workspaceId,
          kind: t.kind,
          version: s?.version,
          createdAt: s?.createdAt,
          totalEntities: total,
          topCounts: top
        } satisfies SnapshotRow;
      }));
      setRows(fetched.sort((a, b) => a.workspaceId.localeCompare(b.workspaceId) || a.kind.localeCompare(b.kind)));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  }
  useEffect(() => { void refresh(); }, []);

  return (
    <div className="page">
      <PageBreadcrumbs />
      <header className="page-head">
        <div>
          <h1>Metadata snapshots</h1>
          <p className="muted">
            Current synced metadata snapshot per workspace and source type. Each row reflects the
            most recent <code>tracker-metadata-sync</code> / <code>aggregate-metadata-sync</code>{" "}
            for that workspace; the platform retains one active snapshot per (workspace, type).
            Use this to spot stale snapshots, missing syncs, or workspaces that haven't onboarded
            their metadata yet.
          </p>
        </div>
        <div className="actions">
          <button type="button" className="btn btn-secondary" onClick={() => void refresh()}>Refresh</button>
        </div>
      </header>

      {error && <div className="ts-alert ts-alert-error">{error}</div>}

      {rows === null && <p className="muted">Loading…</p>}
      {rows && rows.length === 0 && (
        <div className="empty-state-card">
          <h2 style={{ marginTop: 0 }}>No snapshots stored</h2>
          <p>
            No workspace has synced tracker or aggregate metadata yet. Pair a DHIS2 instance for
            any workspace and run <strong>tracker-metadata-sync</strong> /{" "}
            <strong>aggregate-metadata-sync</strong>; rows will appear here once the snapshots land.
          </p>
        </div>
      )}
      {rows && rows.length > 0 && (
        <table className="transfer-table">
          <thead>
            <tr>
              <th>Workspace</th><th>Kind</th><th>Version</th>
              <th>Synced at</th>
              <th style={{ textAlign: "right" }}>Total entities</th>
              <th>Top sections</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={`${r.workspaceId}::${r.kind}`}>
                <td className="mono" style={{ fontSize: 11 }}>{r.workspaceId}</td>
                <td>
                  <span className={`pill ${r.kind === "tracker" ? "pill-info" : "pill-muted"}`}>{r.kind}</span>
                </td>
                <td>{r.version ?? "—"}</td>
                <td>{r.createdAt ? new Date(r.createdAt).toLocaleString() : "—"}</td>
                <td style={{ textAlign: "right" }}>{r.totalEntities.toLocaleString()}</td>
                <td style={{ fontSize: 11 }}>
                  {r.topCounts.length === 0 ? <span className="muted">—</span> : r.topCounts.map((c) => (
                    <span key={c.section} className="muted" style={{ marginRight: 8 }}>
                      <strong>{c.count.toLocaleString()}</strong> {c.section}
                    </span>
                  ))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
